<template>
	<v-container>
		<!-- RESULT VIEW -->
		<div v-if="submitted && resultData">
			<div class="download-wrapper">
				<div class="download-dropdown-wrapper" v-click-outside="closeDropdown">
					<button class="download-btn" @click="toggleDropdown">
						<v-icon x-small left>fas fa-download</v-icon>
						Download VSME Document
						<v-icon x-small right style="margin-left:6px;">
							{{ showDownloadDropdown ? 'fas fa-chevron-up' : 'fas fa-chevron-down' }}
						</v-icon>
					</button>
					<div v-if="showDownloadDropdown" class="download-dropdown-menu">
						<button class="dropdown-item" @click="handleDownloadPDF">
							<v-icon small left class="icon-pdf">fas fa-file-pdf</v-icon>
							As a PDF
						</button>
						<button class="dropdown-item" @click="handleDownloadJSON">
							<v-icon small left class="icon-json">fas fa-file-code</v-icon>
							As a JSON
						</button>
					</div>
				</div>
			</div>
			<v-card class="pa-6 mb-6" outlined>

				<div class="header-bar">
					<div>
						<h2>Business Activity</h2>
					</div>

					<div style="display:flex; gap:8px; align-items:center;">
						<button class="btn-outline" @click="enableEdit">
							<v-icon x-small left>fas fa-pencil</v-icon>
							Edit
						</button>

						<span v-if="isDraft" class="draft-badge">
							<v-icon x-small style="margin-right:4px;">fas fa-clock</v-icon>
							Draft
						</span>

						<span v-else class="submitted-badge">
							<svg width="12" height="12" viewBox="0 0 12 12" fill="none" style="flex-shrink:0">
								<circle cx="6" cy="6" r="5.5" stroke="currentColor" stroke-width="1"/>
								<path d="M3.5 6L5.2 7.7L8.5 4.3" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
							</svg>
							Submitted
						</span>
					</div>
				</div>

				<!-- 1. Business Overview -->
				<div class="section">
					<div class="section-title">1 · Business Overview</div>

					<div class="row-grid">
						<div class="row-item">
							<span class="row-key">Primary business activity</span>
							<span class="row-val">{{ fmt(resultData.primaryActivity) }}</span>
						</div>

						<div class="row-item" v-if="resultData.primaryActivity === 'Other'">
							<span class="row-key">Other activity (specified)</span>
							<span class="row-val">{{ fmt(resultData.primaryActivityOther) }}</span>
						</div>

						<div class="row-item">
							<span class="row-key">NACE sector code</span>
							<span class="row-val">{{ fmt(resultData.naceCode) }}</span>
						</div>

						<div class="row-item">
							<span class="row-key">Sustainability certifications?</span>
							<span class="row-val" v-html="badge(resultData.hasCertifications)"></span>
						</div>
					</div>

					<div
						v-if="resultData.countriesOfOperation"
						class="text-block"
						style="margin-top:1rem;"
					>
						<div class="row-key" style="margin-bottom:4px;">Countries of operation</div>
						<div class="text-body">
							<span v-if="Array.isArray(resultData.countriesOfOperation)">
								{{ resultData.countriesOfOperation.join(", ") }}
							</span>
							<span v-else>
								{{ resultData.countriesOfOperation }}
							</span>
						</div>
					</div>

					<div
						v-if="resultData.hasCertifications === 'Yes' && resultData.certificationsList"
						class="text-block"
						style="margin-top:1rem;"
					>
						<div class="row-key" style="margin-bottom:4px;">Certifications held</div>
						<div class="text-body">{{ resultData.certificationsList }}</div>
					</div>
				</div>

				<!-- 2. Operational Structure -->
				<div class="section">
					<div class="section-title">2 · Operational Structure</div>

					<div class="row-grid">
						<div class="row-item">
							<span class="row-key">Owns production facilities?</span>
							<span class="row-val" v-html="badge(resultData.ownsProduction)"></span>
						</div>

						<div class="row-item">
							<span class="row-key">Outsources operations?</span>
							<span class="row-val" v-html="badge(resultData.hasOutsourcing)"></span>
						</div>

						<div class="row-item" v-if="resultData.hasOutsourcing === 'Yes'">
							<span class="row-key">Outsourced operations (%)</span>
							<span class="row-val">{{ fmt(resultData.outsourcedPct, '%') }}</span>
						</div>
					</div>

					<div
						v-if="resultData.hasOutsourcing === 'Yes' && resultData.outsourcedActivities"
						class="text-block"
						style="margin-top:1rem;"
					>
						<div class="row-key" style="margin-bottom:4px;">Outsourced activities</div>
						<div class="text-body">{{ resultData.outsourcedActivities }}</div>
					</div>
				</div>

				<!-- 3. Supply Chain & Suppliers -->
				<div class="section">
					<div class="section-title">3 · Supply Chain & Suppliers</div>

					<div class="metric-grid">
						<div class="metric-card">
							<p class="metric-label">Active suppliers</p>
							<p class="metric-value">{{ fmt(resultData.activeSuppliers) }}</p>
						</div>

						<div class="metric-card">
							<p class="metric-label">Supplier assessment</p>
							<p class="metric-value">{{ fmt(resultData.supplierAssessment) }}</p>
						</div>

						<div class="metric-card">
							<p class="metric-label">Long-term contracts</p>
							<p class="metric-value">{{ fmt(resultData.longTermContracts) }}</p>
						</div>
					</div>
				</div>
			</v-card>
		</div>

		<!-- FORM VIEW -->
		<v-form v-else ref="form" v-model="formIsValid">
			<v-card class="pa-3 mb-6" outlined>
				<v-alert
					v-if="isDraft"
					type="warning"
					dense
					outlined
					class="mb-3"
					icon="fas fa-clock"
				>
					You have a saved draft. Complete and submit when ready.
				</v-alert>
				<v-col cols="12" class="d-flex justify-center"><h2>BUSINESS ACTIVITY</h2></v-col>

				<!-- 1. Business Overview -->
				<v-row class="contentRow">
					<v-col cols="12"><h2>1. Business Overview</h2></v-col>

					<v-col cols="12">
						<v-select
							v-model="form.primaryActivity"
							:items="['Manufacturing', 'Services', 'Trading', 'Mixed', 'Other']"
							label="Q1. What is your primary business activity?"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" md="6" v-if="form.primaryActivity === 'Other'">
						<v-text-field
							v-model="form.primaryActivityOther"
							label="Please specify"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q1')">
							{{ showDesc.q1 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q1" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS 2 SBM-1, paragraph 42: The undertaking shall disclose its business model, including key activities, products or services, and how these create value over time in relation to sustainability matters.)
						</div>
					</v-col>

					<v-col cols="12">
						<v-text-field
							v-model="form.naceCode"
							label="Q2. What is your company's NACE sector code?"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q2')">
							{{ showDesc.q2 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q2" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS 1, paragraph 25: The undertaking shall classify its economic activities using recognized sector classification systems such as NACE to ensure comparability of sustainability information.)
						</div>
					</v-col>

					<v-col cols="12">
						<v-select
							v-model="form.countriesOfOperation"
							:items="countries"
							label="Q3. In which countries do you operate?"
							multiple
							chips
							clearable
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q3')">
							{{ showDesc.q3 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q3" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS 2 SBM-2, paragraph 48: The undertaking shall disclose the geographical locations of its operations where relevant to understanding material sustainability impacts, risks, and opportunities.)
						</div>
					</v-col>

					<v-col cols="12">
						<v-select
							v-model="form.hasCertifications"
							:items="['Yes', 'No']"
							label="Q4. Does your company hold any sustainability certifications?"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" v-if="form.hasCertifications === 'Yes'">
						<v-textarea
							v-model="form.certificationsList"
							label="List your certifications"
							filled
							dense
							rows="3"
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q4')">
							{{ showDesc.q4 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q4" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS 2 GOV-1, paragraph 21: The undertaking shall disclose governance-related information including sustainability certifications, policies, and externally verified management systems where applicable.)
						</div>
					</v-col>
				</v-row>

				<!-- 2. Operational Structure -->
				<v-row class="contentRow">
					<v-col cols="12"><h2>2. Operational Structure</h2></v-col>

					<v-col cols="12">
						<v-select
							v-model="form.ownsProduction"
							:items="['Yes', 'No']"
							label="Q5. Do you own production facilities?"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q5')">
							{{ showDesc.q5 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q5" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS 2 SBM-2, paragraph 49: The undertaking shall describe its operational structure including owned facilities where significant sustainability impacts occur.)
						</div>
					</v-col>

					<v-col cols="12">
						<v-select
							v-model="form.hasOutsourcing"
							:items="['Yes', 'No']"
							label="Q6. Do you outsource any part of your operations?"
							filled
							dense
						/>
					</v-col>
                    <v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q6')">
							{{ showDesc.q6 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q6" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S2-2, paragraph 61: The undertaking shall disclose activities in its value chain that are outsourced where these may result in material social or environmental impacts.)
						</div>
					</v-col>
					<v-col cols="12" v-if="form.hasOutsourcing === 'Yes'">
						<v-textarea
							v-model="form.outsourcedActivities"
							label="What activities are outsourced?"
							filled
							dense
							rows="3"
						/>
					</v-col>
					<v-col cols="12" v-if="form.hasOutsourcing === 'Yes'">
						<v-text-field
							v-model="form.outsourcedPct"
							label="Q7. What percentage of your operations are outsourced? (%)"
							type="number"
							filled
							dense
						/>
					</v-col>
					<v-col v-if="form.hasOutsourcing === 'Yes'" cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q7')">
							{{ showDesc.q7 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q7" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S2-2, paragraph 62: The undertaking shall provide quantitative information on the extent of outsourced activities where relevant to value chain materiality assessment.)
						</div>
					</v-col>
				</v-row>

				<!-- 3. Supply Chain & Suppliers -->
				<v-row class="contentRow">
					<v-col cols="12"><h2>3. Supply Chain & Suppliers</h2></v-col>

					<v-col cols="12">
						<v-text-field
							v-model="form.activeSuppliers"
							label="Q8. How many active suppliers do you work with?"
							type="number"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q8')">
							{{ showDesc.q8 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q8" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS 2 SBM-2, paragraph 50: The undertaking shall describe the structure of its upstream value chain including the number and nature of suppliers where relevant.)
						</div>
					</v-col>

					<v-col cols="12">
						<v-select
							v-model="form.supplierAssessment"
							:items="['Yes', 'No', 'Partially']"
							label="Q9. Do you assess suppliers based on quality or sustainability criteria?"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q9')">
							{{ showDesc.q9 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q9" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S2-4, paragraph 65: The undertaking shall disclose due diligence processes used to assess suppliers against environmental, social, and quality criteria.)
						</div>
					</v-col>

					<v-col cols="12">
						<v-select
							v-model="form.longTermContracts"
							:items="['Yes', 'No', 'Some']"
							label="Q10. Do you have long-term contracts with suppliers?"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q10')">
							{{ showDesc.q10 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q10" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS 2 MDR-A, paragraph 68: The undertaking shall disclose the nature of its business relationships, including contract duration and stability where relevant to sustainability impacts.)
						</div>
					</v-col>
				</v-row>

				<!-- Error -->
				<v-row class="contentRow" v-if="errorMessage">
					<v-col cols="12">
						<v-alert type="error" dense>{{ errorMessage }}</v-alert>
					</v-col>
				</v-row>

				<!-- Actions -->
				<v-row class="contentRow">
					<v-col cols="12" class="d-flex justify-center">
						<v-btn outlined color="primary" class="mr-5" :loading="loading" :disabled="loading" @click="saveDraft">
							<v-icon x-small left>fas fa-floppy-disk</v-icon>
							Save as Draft
						</v-btn>
						<v-btn color="primary" :loading="loading" :disabled="loading" @click="submit">
							Submit
						</v-btn>
					</v-col>
				</v-row>
			</v-card>
		</v-form>
	</v-container>
</template>

<script>
import CsrAPI from "../api/csr/csr"
import { mapGetters } from "vuex"

export default {
	props: {
		initialData: { type: Object, default: null },
		isEditMode:  { type: Boolean, default: false },
	},

	data() {
		return {
			formIsValid: false,
			loading: false,
			errorMessage: "",
			showDesc: {},
			form: {
				primaryActivity: null,
				primaryActivityOther: "",
				naceCode: "",
				countriesOfOperation: [],
				hasCertifications: null,
				certificationsList: "",
				ownsProduction: null,
				hasOutsourcing: null,
				outsourcedActivities: "",
				outsourcedPct: null,
				activeSuppliers: null,
				supplierAssessment: null,
				longTermContracts: null,
				isDraft: false,
			},
			submitted: false,
			resultData: null,
			isDraft: false,
			hasExistingRecord: false,
			showDownloadDropdown: false,
			countries: [
				"Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", 
				"Argentina", "Armenia", "Australia", "Austria", "Azerbaijan",
				"Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", 
				"Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana",
				"Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", 
				"Cambodia", "Cameroon", "Canada", "Cape Verde", "Cayman Islands", "	Central African Republic",
				"Chad", "Chile", "China", "Christmas Island", "Cocos (Keeling) Islands", "Colombia",
				"Comoros", "Democratic Republic of the Congo", "Congo, Republic of (Brazzaville)", "	Cook Islands",
				"Costa Rica", "Côte D'ivoire (Ivory Coast)", "Croatia", "Cuba", "Cyprus", "	Czech Republic",
				"Denmark", "Djibouti", "Dominica", "Dominican Republic",
				"East Timor (Timor-Leste)", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea",
				"Estonia", "Ethiopia", 
				"Falkland Islands", "Faroe Islands", "Fiji", "Finland", "France", "French Guiana",
				"French Polynesia", "French Southern Territories",
				"Gabon", "The Gambia", "Georgia", "Germany", "Ghana", "Gibraltar",
				"Greece", "Greenland", "Grenada", "Guadeloupe", "Guam", "Guatemala",
				"Guinea", "Guinea-Bissau", "Guyana",
				"Haiti", "Holy See", "Honduras", "Hong Kong", "Hungary",
				"Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland",
				"Israel", "Italy", "Ivory Coast",
				"Jamaica", "Japan", "Jordan", 
				"Kazakhstan", "Kenya", "Kiribati", "Korea, Democratic People's Rep. (North Korea)", "Korea, Republic of (South Korea)", 
				"Kosovo", "Kuwait", "Kyrgyzstan",
				"Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya",
				"Liechtenstein", "Lithuania", "Luxembourg",
				"Macau", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali",
				"Malta", "Marshall Islands", "Martinique", "Mauritania", "Mauritius", "Mayotte",
				"Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro",
				"Montserrat", "Morocco", "Mozambique", "Myanmar", 
				"Namibia", "Nauru", "Nepal", "Netherlands", "Netherlands Antilles", "New Caledonia",
				"New Zealand", "Nicaragua", "Niger", "Nigeria", "Niue", "North Macedonia",
				"Northern Mariana Islands", "Norway", 
				"Oman", 
				"Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay",
				"Peru", "Philippines", "Pitcairn Island", "Poland", "Portugal", "Puerto Rico",
				"Qatar", 
				"Reunion Island", "Romania", "Russian Federation", "Rwanda", 
				"Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa",
				"San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles",
				"Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia",
				"South Africa", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname",
				"Swaziland", "Sweden", "Switzerland", "Syria",
				"Taiwan", "Tajikistan", "Tanzania", "Thailand", "Tibet", "Timor-Leste",
				"Togo", "Tokelau", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey",
				"Turkmenistan", "Turks and Caicos Islands", "Tuvalu", 
				"Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States",
				"Uruguay", "Uzbekistan", 
				"Vanuatu", "Vatican City State", "Venezuela", "Vietnam",
				"Virgin Islands (British)", "Virgin Islands (U.S.)",
				"Wallis and Futuna Islands", "Western Sahara",
				"Yemen",
				"Zambia", "Zimbabwe",
			],
		}
	},

	watch: {
		initialData: {
			immediate: true,
			handler(res) {
				if (!res) return
				this.mapDataToForm(res)
			},
		},
	},

	async created() {
		this.loadData()
	},

	computed: {
		...mapGetters("user", ["isLoggedIn", "getUser"]),
		username() {
			return this.getUser.clientName
		},
	},

	methods: {
		toggleDesc(key) {
			this.$set(this.showDesc, key, !this.showDesc[key])
		},

		enableEdit() {
			this.submitted = false
		},

		async loadData() {
			try {
				const res = await CsrAPI.getVsmeBusiness()
				if (!res || !res.Primary_Activity) {
					this.submitted = false
					return
				}
				this.hasExistingRecord = true
				this.mapDataToForm(res)
				this.resultData = { ...this.form }

				if (res.Is_Draft === true || res.Is_Draft === 1) {
					this.isDraft = true
					this.submitted = false   // reopen form pre-filled
				} else if (!this.isEditMode) {
					this.submitted = true
				}
			} catch (error) {
				console.error("Failed to load business activity data:", error)
			}
		},

		mapDataToForm(res) {
			this.form = {
				...this.form,
				primaryActivity:      res.Primary_Activity       || null,
				primaryActivityOther: res.Primary_Activity_Other || "",
				naceCode:             res.NACE_Code              || "",
				countriesOfOperation: res.Countries_Of_Operation
					? res.Countries_Of_Operation.split(",").map(c => c.trim()).filter(Boolean)
					: [],
				hasCertifications:    res.Has_Certifications     || null,
				certificationsList:   res.Certifications_List    || "",
				ownsProduction:       res.Owns_Production        || null,
				hasOutsourcing:       res.Has_Outsourcing        || null,
				outsourcedActivities: res.Outsourced_Activities  || "",
				outsourcedPct:        res.Outsourced_Pct         || null,
				activeSuppliers:      res.Active_Suppliers       || null,
				supplierAssessment:   res.Supplier_Assessment    || null,
				longTermContracts:    res.Long_Term_Contracts    || null,
			}
		},

		async _saveOrUpdate(isDraft) {
			const f = this.form
			const countries =
				Array.isArray(f.countriesOfOperation)
					? (f.countriesOfOperation || []).join(", ")
					: f.countriesOfOperation || ""
			const args = [
				f.primaryActivity, f.primaryActivityOther, f.naceCode,
				countries, f.hasCertifications, f.certificationsList,
				f.ownsProduction, f.hasOutsourcing, f.outsourcedActivities,
				f.outsourcedPct, f.activeSuppliers, f.supplierAssessment,
				f.longTermContracts,
				isDraft,
			]
			if (this.hasExistingRecord) {
				await CsrAPI.updateVsmeBusiness(...args)
			} else {
				await CsrAPI.saveVsmeBusiness(...args)
				this.hasExistingRecord = true   // flip flag after first insert
			}
		},

		async submit() {
			this.loading = true
			this.errorMessage = ""
			try {
				await this._saveOrUpdate(false)
				this.resultData = { ...this.form }
				this.isDraft = false
				this.submitted = true
				this.$emit("saved")
			} catch (error) {
				this.errorMessage = error.message
			} finally {
				this.loading = false
			}
		},

		async saveDraft() {
			this.loading = true
			this.errorMessage = ""
			try {
				await this._saveOrUpdate(true)
				this.isDraft = true
				this.$emit("drafted")
			} catch (error) {
				this.errorMessage = error.message
			} finally {
				this.loading = false
			}
		},

		toggleDropdown() {
			this.showDownloadDropdown = !this.showDownloadDropdown
		},

		closeDropdown() {
			this.showDownloadDropdown = false
		},

		handleDownloadPDF() {
			this.showDownloadDropdown = false
			CsrAPI.downloadVsmePDF()
		},

		handleDownloadJSON() {
			this.showDownloadDropdown = false
			CsrAPI.downloadJSON()
		},

		fmt(val, suffix = "") {
			if (val === null || val === undefined || val === "") {
				return "—"
			}
			return val + suffix
		},

		badge(val) {
			if (val === "Yes") {
				return "<span class='yes-badge'>Yes</span>"
			}
			if (val === "No") {
				return "<span class='no-badge'>No</span>"
			}
			return val || "—"
		},

	},
}
</script>

<style scoped>
.esgcontainer h1 {
  color: #1e7c83;
  font-weight: bold;
}

h2 {
  color: #1e7c83;
}

h3 {
  color: #424242;
  margin-bottom: 8px;
}

.contentRow {
  margin-bottom: 16px;
}

.icon-circle {
	border: 1px solid currentColor;
	border-radius: 50%;
	padding: 4px;
}

.desc-wrapper {
	padding-top: 0 !important;
	margin-top: -20px;
}

.header-bar {
	display: flex;
	align-items: right;
	justify-content: space-between;
	margin-bottom: 1.25rem;
}

.submitted-badge {
	display: flex;
	align-items: center;
	gap: 5px;
	background: #e8f5e9;
	color: #2e7d32;
	padding: 4px 12px;
	border-radius: 20px;
}

.btn-outline {
	border: 1px solid #bdbdbd;
	background: none;
	border-radius: 6px;
	padding: 5px 12px;
	color: #424242;
	cursor: pointer;
}

.btn-outline:hover {
	background: #f5f5f5;
}

.download-wrapper {
	display: flex;
	justify-content: flex-end;
	margin-bottom: 10px; 
}

.download-btn {
	border: 1px solid #bdbdbd;
	background: none;
	border-radius: 6px;
	padding: 5px 12px;
	color: #424242;
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 4px;
}

.download-dropdown-wrapper {
    position: relative;
    display: inline-block;
}

.download-dropdown-menu {
    position: absolute;
    right: 0;
    top: calc(100% + 4px);
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.10);
    min-width: 160px;
    z-index: 100;
    overflow: hidden;
}

.dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 10px 16px;
    background: none;
    border: none;
    color: #424242;
    cursor: pointer;
    text-align: left;
    font-size: 0.875rem;
    transition: background 0.15s;
}

.dropdown-item:hover {
    background: #f5f5f5;
}

.icon-pdf {
    color: #e53935 !important;  
}

.icon-json {
    color: #f9a825 !important;
}

.section {
	background: #fff;
	border: 1px solid #2d9b9b;
	border-radius: 10px;
	padding: 1.25rem 1.5rem;
	margin-bottom: 1rem;
}

.section-title {
	color: #9e9e9e;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	margin: 0 0 1rem;
}

.metric-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
	gap: 10px;
	margin-bottom: 0.5rem;
}

.metric-card {
	background: #f5f5f5;
	border-radius: 8px;
	padding: 0.75rem 1rem;
}

.metric-label {
	color: #757575;
	margin: 0 0 4px;
}

.metric-value {
	color: #212121;
	margin: 0;
}

.row-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
	gap: 12px;
}

.row-item {
	display: flex;
	flex-direction: column;
	gap: 3px;
}

.row-key {
	color: #9e9e9e;
}

.row-val {
	color: #212121;
}

.tag-list {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}

.tag {
	background: #e0f2f1;
	color: #00695c;
	padding: 3px 10px;
	border-radius: 20px;
}

.empty-note {
	color: #bdbdbd;
}

.yes-badge {
	background: #e8f5e9;
	color: #2e7d32;
	padding: 2px 9px;
	border-radius: 20px;
	display: inline-block;
}

.no-badge {
	background: #f5f5f5;
	color: #757575;
	padding: 2px 9px;
	border-radius: 20px;
	display: inline-block;
}

.text-block {
	background: #f9f9f9;
	border-radius: 6px;
	padding: 10px 14px;
}

.text-body {
	color: #424242;
	line-height: 1.6;
}

.sub-label {
	color: #9e9e9e;
	margin-bottom: 8px;
}

.divider {
	border: none;
	border-top: 0.5px solid #e0e0e0;
	margin: 1rem 0;
}

.draft-badge {
    display: flex;
    align-items: center;
    gap: 5px;
    background: #fff8e1;
    color: #f57f17;
    padding: 4px 12px;
    border-radius: 20px;
}

@media print {
  .btn-outline { display: none !important; }
}
</style>